// Water volumes (decision 0044: "water volumes with a surface height, in @rpg/collision"). A volume is an axis-aligned
// box with a surface height: a pond, a lake, one reach of a river. They are plain data beside the Rapier world, not
// Rapier colliders: a point query is a box test, exact and deterministic, answers at once (no step needed), and costs
// nothing in the physics step. Swimming, wading, buoyancy and splashes read them; none of those rules live here.
//
// Spike 002 C4 adds shapes inside the box (decision 0048: "a stream and a pond"): a polygon (a pond's outline, a flat
// surface) and a channel (a stream's course: points with a half width and a surface height each, so the water runs
// downhill). The box stays the broad bound; `waterPolygon` and `waterChannel` build a volume with its box filled in.
// A volume without a shape is the box, as before. `waterShapeDistance` and `waterShapeSurface` answer for one volume,
// so a game can build the water's mesh from the same numbers the queries use.
import type { Vec3 } from "./world";

/** A course point of a channel: where it runs, how wide it is either side, and its surface height there. */
export interface ChannelPoint {
  x: number;
  z: number;
  /** Half the channel's width (m). */
  halfWidth: number;
  /** The water's surface height here (m); between points it runs linearly. */
  surface: number;
}

/** The water's outline inside a volume's box. */
export type WaterShape =
  /** An outline in x and z (in order, either winding; closed), with the volume's flat surface. */
  | { type: "polygon"; points: readonly (readonly [number, number])[] }
  /** A course of points joined by straight reaches, each as wide as its ends (rounded at every point). */
  | { type: "channel"; points: readonly ChannelPoint[] };

/** One body of water: everything inside the box's x and z (and the shape, when it has one), from `min.y` up to the
 *  surface, is water. */
export interface WaterVolume {
  /** A stable ID the game picks. */
  id: string;
  min: Vec3;
  max: Vec3;
  /** The water's surface height (m). Default `max.y`. A channel's surface comes from its points instead. */
  surface?: number;
  /** A surface tag ("water", "swamp"), returned by queries. */
  tag?: string;
  /** The outline inside the box (default: the whole box). */
  shape?: WaterShape;
}

/** A point in water: which volume, its surface, how deep the point is under it (≥ 0) and its tag. */
export interface WaterAt {
  id: string;
  surface: number;
  depth: number;
  tag: string | null;
}

/** Query options. */
export interface WaterQuery {
  /** Planted defect (tests): answer by the box alone, ignoring the shape (a stream's bank reads as water). */
  boxOnly?: boolean;
}

/** The water volumes of one world, in the order added. */
export class WaterVolumes {
  private list: WaterVolume[] = [];

  /** Add a volume. Throws if the ID is taken, the box is empty or the shape is malformed. */
  add(v: WaterVolume): void {
    if (this.list.some((w) => w.id === v.id)) throw new Error(`water: volume "${v.id}" already exists`);
    if (!(v.max.x > v.min.x && v.max.z > v.min.z && v.max.y >= v.min.y)) throw new Error(`water: volume "${v.id}" is empty`);
    const bad = checkWaterShape(v.shape);
    if (bad) throw new Error(`water: volume "${v.id}": ${bad}`);
    this.list.push(copy(v));
  }

  /** Remove a volume; unknown IDs are ignored. */
  remove(id: string): void {
    this.list = this.list.filter((w) => w.id !== id);
  }

  /** Every volume, copied. */
  all(): WaterVolume[] {
    return this.list.map(copy);
  }

  /** Replace every volume (a restore). */
  set(vs: readonly WaterVolume[]): void {
    this.list = vs.map(copy);
  }

  /** The water at a point: inside a volume's x and z (and shape), between its floor and its surface. The deepest wins
   *  when volumes overlap. Null on dry land or above the surface. */
  at(p: Vec3, q: WaterQuery = {}): WaterAt | null {
    let best: WaterAt | null = null;
    for (const w of this.list) {
      if (p.x < w.min.x || p.x > w.max.x || p.z < w.min.z || p.z > w.max.z || p.y < w.min.y) continue;
      const surface = q.boxOnly ? boxSurface(w) : waterShapeSurface(w, p.x, p.z);
      if (surface === null || p.y > surface) continue;
      const depth = surface - p.y;
      if (!best || depth > best.depth) best = { id: w.id, surface, depth, tag: w.tag ?? null };
    }
    return best;
  }

  /** The highest water surface over (x, z), whatever the height; null where there is no water. */
  surfaceAt(x: number, z: number, q: WaterQuery = {}): number | null {
    let s: number | null = null;
    for (const w of this.list) {
      if (x < w.min.x || x > w.max.x || z < w.min.z || z > w.max.z) continue;
      const h = q.boxOnly ? boxSurface(w) : waterShapeSurface(w, x, z);
      if (h !== null && (s === null || h > s)) s = h;
    }
    return s;
  }
}

const boxSurface = (w: WaterVolume): number => w.surface ?? w.max.y;

/** What is wrong with a shape (null when fine). */
export function checkWaterShape(s: WaterShape | undefined): string | null {
  if (!s) return null;
  if (s.type === "polygon") {
    if (s.points.length < 3) return "a polygon needs at least 3 points";
    if (s.points.some((p) => !Number.isFinite(p[0]) || !Number.isFinite(p[1]))) return "a polygon point is not finite";
    return null;
  }
  if (s.points.length < 2) return "a channel needs at least 2 points";
  for (const p of s.points) {
    if (![p.x, p.z, p.halfWidth, p.surface].every(Number.isFinite)) return "a channel point is not finite";
    if (p.halfWidth <= 0) return "a channel's half width must be > 0";
  }
  return null;
}

/**
 * How far inside a volume's outline (x, z) is, in metres: positive inside, negative outside, 0 on the shore. A
 * volume without a shape measures to its box's edges. A game builds its water mesh (and its shore foam) from this.
 */
export function waterShapeDistance(w: WaterVolume, x: number, z: number): number {
  const s = w.shape;
  if (!s) return Math.min(x - w.min.x, w.max.x - x, z - w.min.z, w.max.z - z);
  if (s.type === "polygon") {
    const pts = s.points;
    let inside = false;
    let d2 = Infinity;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      const [xi, zi] = pts[i]!;
      const [xj, zj] = pts[j]!;
      if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
      d2 = Math.min(d2, segDist2(x, z, xj, zj, xi, zi).d2);
    }
    const d = Math.sqrt(d2);
    return inside ? d : -d;
  }
  return channelNearest(s.points, x, z).inside;
}

/** The water's surface over (x, z) for one volume: null outside its box or outline. */
export function waterShapeSurface(w: WaterVolume, x: number, z: number): number | null {
  if (x < w.min.x || x > w.max.x || z < w.min.z || z > w.max.z) return null;
  const s = w.shape;
  if (!s) return boxSurface(w);
  if (s.type === "polygon") return waterShapeDistance(w, x, z) >= 0 ? boxSurface(w) : null;
  const n = channelNearest(s.points, x, z);
  return n.inside >= 0 ? n.surface : null;
}

/** The water's level over (x, z) whether or not the point is inside the outline: the flat surface, or a channel's
 *  nearest reach (a mesh's vertices just past the shore need one). */
export function waterLevel(w: WaterVolume, x: number, z: number): number {
  const s = w.shape;
  if (s?.type === "channel") return channelNearest(s.points, x, z).surface;
  return boxSurface(w);
}

/** A pond (or any flat body) from its outline: the box spans the outline, `depth` below the surface. */
export function waterPolygon(id: string, points: readonly (readonly [number, number])[], surface: number, depth: number, tag = "water"): WaterVolume {
  const xs = points.map((p) => p[0]);
  const zs = points.map((p) => p[1]);
  return {
    id,
    min: { x: Math.min(...xs), y: surface - depth, z: Math.min(...zs) },
    max: { x: Math.max(...xs), y: surface, z: Math.max(...zs) },
    surface,
    tag,
    shape: { type: "polygon", points: points.map((p) => [p[0], p[1]] as const) },
  };
}

/** A stream (or any course) from its points: the box spans the course and its widths, `depth` below its lowest surface. */
export function waterChannel(id: string, points: readonly ChannelPoint[], depth: number, tag = "water"): WaterVolume {
  const lo = (f: (p: ChannelPoint) => number): number => Math.min(...points.map(f));
  const hi = (f: (p: ChannelPoint) => number): number => Math.max(...points.map(f));
  const top = hi((p) => p.surface);
  return {
    id,
    min: { x: lo((p) => p.x - p.halfWidth), y: lo((p) => p.surface) - depth, z: lo((p) => p.z - p.halfWidth) },
    max: { x: hi((p) => p.x + p.halfWidth), y: top, z: hi((p) => p.z + p.halfWidth) },
    surface: top,
    tag,
    shape: { type: "channel", points: points.map((p) => ({ ...p })) },
  };
}

function segDist2(px: number, pz: number, ax: number, az: number, bx: number, bz: number): { d2: number; t: number } {
  const dx = bx - ax;
  const dz = bz - az;
  const l2 = dx * dx + dz * dz;
  const t = l2 > 0 ? Math.min(1, Math.max(0, ((px - ax) * dx + (pz - az) * dz) / l2)) : 0;
  const ex = ax + dx * t - px;
  const ez = az + dz * t - pz;
  return { d2: ex * ex + ez * ez, t };
}

/** The reach nearest (x, z) by how far inside it the point is: the signed distance and the surface there. */
function channelNearest(pts: readonly ChannelPoint[], x: number, z: number): { inside: number; surface: number } {
  let best = { inside: -Infinity, surface: pts[0]!.surface };
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const { d2, t } = segDist2(x, z, a.x, a.z, b.x, b.z);
    const inside = a.halfWidth + (b.halfWidth - a.halfWidth) * t - Math.sqrt(d2);
    if (inside > best.inside) best = { inside, surface: a.surface + (b.surface - a.surface) * t };
  }
  return best;
}

function copy(v: WaterVolume): WaterVolume {
  const c: WaterVolume = { ...v, min: { ...v.min }, max: { ...v.max } };
  if (v.shape) c.shape = v.shape.type === "polygon" ? { type: "polygon", points: v.shape.points.map((p) => [p[0], p[1]] as const) } : { type: "channel", points: v.shape.points.map((p) => ({ ...p })) };
  return c;
}
